import { Injectable, Optional } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { map, tap, withLatestFrom } from 'rxjs/operators';
import { RolesStore } from '@telespot/authentication/ui';
import { PipelineIntegrityService, PipelineStepCategory } from '@protocols/domain';

import { PipelineCategoryEffects } from '../../../effects/pipeline-category.effects';
import { PipelineStepEditorEffects } from '../../../effects/pipeline-step-editor.effects';
import { AvailableTask, PipelineTaskSelectionEffects } from '../../../effects/pipeline-task-selection.effects';
import { AlgorithmSwitcherState } from '../../../state/algorithm-switcher.state';
import { MethodTypeState, MethodTypeStateItem } from '../../../state/method-type.state';
import {
  PendingPersistence,
  PipelineCategoryResult,
  PipelineCategoryVariant,
} from '../../../state/pipeline-category.state';
import { PipelineStepEditorState } from '../../../state/pipeline-step-editor.state';
import { MethodTypeSelectorItem } from '../../method-types/method-type-selector/method-type-selector.component';

/**
 * Filed under the editor's warnings namespace rather than its errors one: it
 * describes a property of the step being edited, not a complaint about the last
 * action the user took.
 */
const UNDEFINED_LABELS_WARNING_KEY = 'pipeline_step_editor.warnings.undefined_labels';

export interface CategoryListItemVM {
  uuid: string;
  name: string;
  /**
   * Whether this option's label is not properly defined.
   */
  undefinedLabel: boolean;
}

export interface CategoryEditorContext {
  variant: PipelineCategoryVariant;
  methodType: MethodTypeSelectorItem | null;
  supportsLabelColors: boolean;
  initialCategory?: {
    uuid: string;
    name: PipelineStepCategory['name'];
    pendingPersistence: PendingPersistence;
    options: {
      uuid: string;
      name: PipelineStepCategory['name'];
      thrdis: number;
      pendingPersistence: PendingPersistence;
    }[];
  };
}

@Injectable()
export class PipelineStepEditorFacade {
  constructor(
    private readonly editorEffects: PipelineStepEditorEffects,
    private readonly taskSelectionEffects: PipelineTaskSelectionEffects,
    private readonly state: PipelineStepEditorState,
    private readonly algorithmSwitcherState: AlgorithmSwitcherState,
    private readonly categoryEffects: PipelineCategoryEffects,
    private readonly rolesStore: RolesStore,
    @Optional() private readonly methodTypeState: MethodTypeState | null
  ) {}

  /** Triggered by the component on init — I/O is driven by lifecycle, not DI. */
  public readonly loadRoles = this.state.effect<void>((trigger$) =>
    trigger$.pipe(tap(() => this.rolesStore.fetchRoles()))
  );

  /**
   * Only admins may modify pipeline steps; everyone else sees a readonly editor.
   */
  public readonly isAdmin$ = this.rolesStore.isAdmin$;

  public readonly methodType$: Observable<MethodTypeStateItem | null> = this.methodTypeState
    ? this.methodTypeState.methodType$
    : of(null);

  // ============================================================================
  // Selectors — passthrough
  // ============================================================================

  public readonly context$ = this.state.context$;
  public readonly environment$ = this.state.environment$;
  public readonly editingStepId$ = this.state.editingStepId$;
  public readonly task$ = this.state.task$;
  public readonly cloudSubject$ = this.state.cloudSubject$;
  public readonly generateFinding$ = this.state.generateFinding$;
  public readonly categories$ = this.state.categories$;
  public readonly editingCategoryUuid$ = this.state.editingCategoryUuid$;
  public readonly multiple$ = this.state.multiple$;
  public readonly allowNewOptions$ = this.state.allowNewOptions$;
  public readonly enableMosaicView$ = this.state.enableMosaicView$;
  public readonly counter$ = this.state.counter$;
  public readonly manualMode$ = this.state.manualMode$;
  public readonly manualParams$ = this.state.manualParams$;
  public readonly manualParamsValid$ = this.state.manualParamsValid$;
  public readonly manualParamsStructuredCompatible$ = this.state.manualParamsStructuredCompatible$;
  public readonly loading$ = this.state.loading$;
  public readonly confirmed$ = this.editorEffects.confirmed$;

  // ============================================================================
  // Derived selectors
  // ============================================================================

  /**
   * What the editor's error banner shows. A complaint about the last action
   * wins while it is set — the user just did the thing it is about — and the
   * label-name lookup failure shows through once it clears, because that one
   * stays outstanding until the next lookup rather than until the next click.
   */
  public readonly error$: Observable<string | null> = this.state.select(
    this.state.error$,
    this.state.labelNamesError$,
    (error: string | null, labelNamesError: string | null) => error ?? labelNamesError
  );

  /**
   * The standing warning that some label this step references is not properly
   * defined — shown by id only, and a real risk of offering analysts an
   * unnamed choice.
   *
   * Derived from what the form currently holds rather than raised as an event.
   * As an event it would be cleared as a side effect of the next successful
   * mutation — every updater that replaces the category list clears `error` by
   * design — and so would vanish while the problem remained. Derived, it
   * appears the moment such a step is opened, clears itself the moment the
   * labels are named, and cannot go stale. It warns only: `canSubmit$` does
   * not read it, because a uuid pointing at a label nobody has created yet is
   * legitimate work in progress.
   *
   * Suppressed while a label-name lookup failure is outstanding. This stream
   * asserts a cause — the labels are badly defined — which is false when the
   * read merely failed, and the two messages must never appear together.
   * Suppression reads the state's own `labelNamesError$` rather than this
   * facade's `error$`: the latter is masked by any complaint about the last
   * action, which would un-suppress the warning at exactly the wrong moment.
   */
  public readonly undefinedLabelsWarning$: Observable<string | null> = this.state.select(
    this.state.categories$,
    this.state.labelNamesError$,
    (categories: PipelineStepCategory[], labelNamesError: string | null) => {
      if (labelNamesError !== null) return null;
      const anyUndefined = categories.some((category) => category.undefinedLabelUuids().length > 0);
      return anyUndefined ? UNDEFINED_LABELS_WARNING_KEY : null;
    }
  );

  public readonly availableTasks$: Observable<AvailableTask[]> = this.taskSelectionEffects.availableTasks$;

  public readonly algorithmRequired$: Observable<boolean> = this.state.select(this.state.task$, (task) =>
    task ? PipelineIntegrityService.algorithmRequired(task) : false
  );

  public readonly categoryEditorVariant$: Observable<PipelineCategoryVariant> = this.state.select(
    this.state.task$,
    (task) => (task !== null && PipelineIntegrityService.isBasicCategoryTask(task) ? 'basic' : 'options')
  );

  public readonly canAddCategory$: Observable<boolean> = this.state.select(
    this.state.task$,
    this.state.categories$,
    (task, categories) => {
      if (task !== null && PipelineIntegrityService.isSingleCategoryTask(task)) {
        return categories.length === 0;
      }
      return true;
    }
  );

  public readonly canAddCategoryAllowed$: Observable<boolean> = this.state.select(
    this.canAddCategory$,
    this.rolesStore.isAdmin$,
    (canAdd, isAdmin) => canAdd && isAdmin
  );

  public readonly canEnterManualMode$: Observable<boolean> = this.state.select(
    this.state.manualMode$,
    this.rolesStore.isAdmin$,
    (manualMode, isAdmin) => !manualMode && isAdmin
  );

  public readonly selectedAlgorithmId$ = this.algorithmSwitcherState.algorithmToSwitchToId$;

  /**
   * The category rows, each carrying whether it is one the standing warning is
   * about.
   *
   * `undefinedLabel` is a pure function of the categories, deliberately NOT
   * gated on `labelNamesError$` the way `undefinedLabelsWarning$` is. The
   * category editor marks its option rows from its own state, which has no
   * reach into this one's failure flag — and it cannot be given one that stays
   * honest, since a flag snapshotted when the dialog opens would go stale the
   * moment the next lookup succeeded. Gating only here would leave the two
   * lists disagreeing about one condition: a category reading clean while every
   * option inside it is marked. The single suppression the spec asks for lives
   * on the banner, which is what states the cause.
   */
  public readonly categoryItems$: Observable<CategoryListItemVM[]> = this.state.select(
    this.state.categories$,
    (categories) =>
      categories.map<CategoryListItemVM>((c) => ({
        uuid: c.uuid,
        name: c.name.en ?? c.uuid,
        undefinedLabel: c.undefinedLabelUuids().length > 0,
      }))
  );

  public readonly showAlgorithmSection$: Observable<boolean> = this.state.select(
    this.state.environment$,
    this.state.task$,
    (env, task) => (env !== null && task !== null ? PipelineIntegrityService.algorithmCompatible(env, task) : false)
  );

  public readonly showCategoriesSection$: Observable<boolean> = this.state.select(
    this.state.environment$,
    this.state.task$,
    (env, task) => (env !== null && task !== null ? PipelineIntegrityService.categoryRequired(env, task) : false)
  );

  public readonly showCloudSubject$: Observable<boolean> = this.state.select(
    this.state.environment$,
    this.state.task$,
    (env, task) => env !== null && task !== null && PipelineIntegrityService.showsCloudSubject(env)
  );

  public readonly showGenerateFinding$: Observable<boolean> = this.state.select(
    this.state.environment$,
    this.state.task$,
    (env, task) => env !== null && task !== null && PipelineIntegrityService.showsGenerateFinding(env)
  );

  public readonly showMultipleCheckbox$: Observable<boolean> = this.state.select(
    this.state.environment$,
    this.state.task$,
    (env, task) => (env !== null && task !== null ? PipelineIntegrityService.supportsMultiple(env, task) : false)
  );

  public readonly showAllowNewOptionsCheckbox$: Observable<boolean> = this.state.select(
    this.state.environment$,
    this.state.task$,
    (env, task) => (env !== null && task !== null ? PipelineIntegrityService.supportsAllowNewOptions(env, task) : false)
  );

  public readonly showMosaicViewCheckbox$: Observable<boolean> = this.state.select(
    this.state.environment$,
    this.state.task$,
    (env, task) => (env !== null && task !== null ? PipelineIntegrityService.supportsMosaicView(env, task) : false)
  );

  public readonly showCounterCheckbox$: Observable<boolean> = this.state.select(
    this.state.environment$,
    this.state.task$,
    (env, task) => (env !== null && task !== null ? PipelineIntegrityService.supportsCounter(env, task) : false)
  );

  public readonly showMappingPlaceholder$: Observable<boolean> = this.state.select(
    this.state.environment$,
    this.state.task$,
    (env, task) => (env !== null && task !== null ? PipelineIntegrityService.isMappingStep(env, task) : false)
  );

  public readonly showStructuredSections$: Observable<boolean> = this.state.select(
    this.state.task$,
    this.state.manualMode$,
    (task, manualMode) => task !== null && !manualMode
  );

  public readonly hasChanges$: Observable<boolean> = this.state.select(
    this.editorEffects.currentSignature$,
    this.state.baselineSignature$,
    (current, baseline) => baseline !== null && current !== baseline
  );

  /**
   * Whether committing the step requires a confirmation dialog first. Manual
   * Mode saves persist hand-edited JSON, so the component warns before saving;
   * structured saves commit directly. The rule lives here, not in the view.
   */
  public readonly saveRequiresConfirmation$ = this.manualMode$;

  public readonly categoryEditorContext$: Observable<CategoryEditorContext> = this.state.select(
    this.state.editingCategoryUuid$,
    this.state.categories$,
    this.methodType$,
    this.categoryEditorVariant$,
    this.state.environment$,
    this.state.task$,
    (editingUuid, categories, methodType, variant, env, task) => {
      const editing = editingUuid ? categories.find((c) => c.uuid === editingUuid) : undefined;
      return {
        variant,
        methodType: this.toSelectorItem(methodType),
        supportsLabelColors:
          env !== null && task !== null ? PipelineIntegrityService.supportsLabelColors(env, task) : true,
        initialCategory: editing
          ? {
              uuid: editing.uuid,
              name: editing.name,
              pendingPersistence: undefined,
              options: editing.options.map((o) => ({
                uuid: o.uuid,
                name: o.name,
                thrdis: o.thrdis,
                pendingPersistence: undefined,
              })),
            }
          : undefined,
      };
    }
  );

  public readonly canSubmit$: Observable<boolean> = this.state.select(
    this.state.task$,
    this.state.environment$,
    this.algorithmSwitcherState.algorithmToSwitchToId$,
    this.state.cloudSubject$,
    this.state.generateFinding$,
    this.state.categories$,
    this.state.loading$,
    this.state.manualMode$,
    this.state.manualParamsValid$,
    this.rolesStore.isAdmin$,
    (
      task,
      environment,
      algoId,
      cloudSubject,
      generateFinding,
      categories,
      loading,
      manualMode,
      manualValid,
      isAdmin
    ) => {
      if (!isAdmin) return false;
      if (loading || !task || !environment) return false;
      if (PipelineIntegrityService.showsCloudSubject(environment) && !cloudSubject) return false;
      if (PipelineIntegrityService.showsGenerateFinding(environment) && !generateFinding) {
        return false;
      }
      if (PipelineIntegrityService.algorithmRequired(task) && !algoId) return false;
      if (manualMode) return manualValid;
      if (PipelineIntegrityService.categoryRequired(environment, task) && categories.length === 0) {
        return false;
      }
      return true;
    }
  );

  // ============================================================================
  // Effects + updaters — passthrough
  // ============================================================================

  public readonly loadStep = this.editorEffects.loadStep;
  public readonly selectTask = this.taskSelectionEffects.selectTask;
  public readonly upsertCategory = this.editorEffects.upsertCategory;
  public readonly save = this.editorEffects.save;
  public readonly cancel = this.editorEffects.cancel;
  public readonly enterManualMode = this.editorEffects.enterManualMode;
  public readonly exitManualMode = this.editorEffects.exitManualMode;

  public readonly setCloudSubject = this.state.setCloudSubject;
  public readonly setGenerateFinding = this.state.setGenerateFinding;
  public readonly removeCategory = this.state.removeCategory;
  public readonly reorderCategories = this.state.reorderCategories;
  public readonly setEditingCategoryUuid = this.state.setEditingCategoryUuid;
  public readonly setMultiple = this.state.setMultiple;
  public readonly setAllowNewOptions = this.state.setAllowNewOptions;
  public readonly setEnableMosaicView = this.state.setEnableMosaicView;
  public readonly setCounter = this.state.setCounter;
  public readonly setManualParams = this.state.setManualParams;

  // ============================================================================
  // Effects — category-dialog choreography
  // ============================================================================

  private readonly _openCategoryDialog$ = new Subject<CategoryEditorContext>();

  /**
   * One-shot signal emitted when `beginEditingCategory` resolves the editor
   * context — the consuming component subscribes once and opens the dialog
   * in response.
   */
  public readonly openCategoryDialog$: Observable<CategoryEditorContext> = this._openCategoryDialog$.asObservable();

  public readonly beginEditingCategory = this.state.effect((uuid$: Observable<string | null>) =>
    uuid$.pipe(
      map((uuid) => this.state.setEditingCategoryUuid(uuid)),
      withLatestFrom(this.categoryEditorContext$, this.rolesStore.isAdmin$),
      map(([, ctx, isAdmin]) => {
        // Seed through the effects (not `categoryState.initialize`) so the
        // session gets a dirty-tracking baseline captured with the seed.
        this.categoryEffects.initialize({
          variant: ctx.variant,
          readonly: !isAdmin,
          methodType: ctx.methodType,
          supportsLabelColors: ctx.supportsLabelColors,
          initialCategory: ctx.initialCategory,
        });
        this._openCategoryDialog$.next(ctx);
      })
    )
  );

  public readonly completeEditingCategory = this.state.effect((result$: Observable<PipelineCategoryResult | null>) =>
    result$.pipe(
      map((result) => {
        if (!result) {
          this.state.setEditingCategoryUuid(null);
          return;
        }
        this.editorEffects.upsertCategory(
          new PipelineStepCategory({
            uuid: result.category.uuid,
            name: result.category.name,
            options: result.options.map((o) => ({
              uuid: o.uuid,
              name: o.name,
              thrdis: o.thrdis,
            })),
          })
        );
      })
    )
  );

  private toSelectorItem(methodType: MethodTypeStateItem | null): MethodTypeSelectorItem | null {
    if (!methodType || !methodType.name) return null;
    return { id: methodType.id, name: methodType.name, type: methodType.type };
  }
}
